import React, { useState } from 'react';
import ChapterTreePicker from '../scheduleBuilder/ChapterTreePicker';
import CoursePicker from '../scheduleBuilder/CoursePicker';
import ContentRangePicker from '../scheduleBuilder/ContentRangePicker';
import { createScheduleSession } from '../../../api/adminApi';

// `Offline` is a class taught in person: it links no content, and the contents
// picker below offers none for it. The Ideal School batches run offline from
// June onward.
const MODES = ['Recorded', 'Live', 'Offline', 'Unit Test', 'Exam'];

/**
 * Adds one class to the open calendar day, for every batch in the group.
 *
 * The calendar shows a whole batch group at once, and a class is authored for
 * the group rather than per batch, so one submit writes one `Schedule` row per
 * batch — the same shape the Month Planner writes, through the same upsert, so
 * re-adding an identical session updates it instead of duplicating it.
 *
 * **Contents are optional.** A Live class or an exam is routinely scheduled
 * days before the video conference or exam it will point at has been created —
 * that is the whole reason schedule healing exists — so the form submits
 * happily with nothing selected, and the day drawer's existing "Link content"
 * panel attaches the content once it appears.
 */
/**
 * The course a month is planned from, which `groupCourses` sorts first — the
 * group also holds add-ons (`AI 4 Kerala`, `XANDY MAT MAGIC`) on every batch,
 * so leaving the picker empty made the operator hunt for the syllabus course.
 */
const defaultCourseId = (courses) => {
  const primary = courses.find((course) => course.isPrimary) || courses[0];
  return primary ? String(primary.id) : '';
};

const AddSessionPanel = ({ isoDate, batchIds, groupLabel, courses = [], onCreated }) => {
  const [open, setOpen] = useState(false);
  const [courseId, setCourseId] = useState(() => defaultCourseId(courses));
  const [chapter, setChapter] = useState(null);
  const [contentIds, setContentIds] = useState([]);
  const [subject, setSubject] = useState('');
  const [mode, setMode] = useState('Recorded');
  const [time, setTime] = useState('18:30');
  const [classNumber, setClassNumber] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [section, setSection] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => {
    setCourseId(defaultCourseId(courses));
    setChapter(null);
    setContentIds([]);
    setSubject('');
    setMode('Recorded');
    setTime('18:30');
    setClassNumber('');
    setDurationMinutes('60');
    setSection('');
    setError(null);
    setResult(null);
  };

  const canSubmit = subject.trim() !== ''
    && Number(classNumber) >= 1
    && batchIds.length > 0
    && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createScheduleSession({
        batchIds,
        date: isoDate,
        subject: subject.trim(),
        classNumber: Number(classNumber),
        contentIds,
        time: time.trim() || null,
        mode,
        durationMinutes: Number(durationMinutes) || null,
        section: section.trim() || null,
      });
      setResult(created);
      if (onCreated) onCreated(created);
    } catch (submitError) {
      setError(submitError?.response?.data?.message || 'Could not create the session.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="cal-addsession-bar">
        <button
          type="button"
          className="sb-btn"
          onClick={() => { reset(); setOpen(true); }}
          disabled={batchIds.length === 0}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" /> Add a session
        </button>
        <span className="sb-selcount">
          {batchIds.length === 0
            ? 'Pick a batch group first'
            : `Writes one class on ${isoDate} for all ${batchIds.length} batches`}
        </span>
      </div>
    );
  }

  if (result) {
    return (
      <div className="cal-addsession">
        <div className="sb-result">
          Added — {result.created} created, {result.updated} updated across{' '}
          {result.batches} batches
          {result.contents > 0 ? `, ${result.contents} contents linked` : ', no content linked yet'}.
        </div>
        <div className="sb-actions">
          <button type="button" className="sb-btn" onClick={reset}>Add another</button>
          <button type="button" className="sb-btn" onClick={() => { reset(); setOpen(false); }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cal-addsession">
      <div className="sb-panel-head">
        <h4>Add a session · {isoDate}</h4>
        <span className="sb-selcount">
          {groupLabel ? `${groupLabel} — ` : ''}{batchIds.length} batches
        </span>
      </div>

      <div className="sb-form">
        <div className="sb-field">
          <label htmlFor="add-session-subject">Subject</label>
          <input
            id="add-session-subject"
            type="text"
            placeholder="Physics"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
        </div>

        <div className="sb-field">
          <label htmlFor="add-session-mode">Mode</label>
          <select
            id="add-session-mode"
            value={mode}
            onChange={(event) => {
              setMode(event.target.value);
              // The contents offered depend on the mode, so a selection made
              // under the old one may no longer be linkable.
              setContentIds([]);
            }}
          >
            {MODES.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>

        <div className="sb-field">
          <label htmlFor="add-session-time">Time</label>
          <input
            id="add-session-time"
            type="text"
            value={time}
            onChange={(event) => setTime(event.target.value)}
          />
        </div>

        <div className="sb-field">
          <label htmlFor="add-session-class">Class number</label>
          <input
            id="add-session-class"
            type="number"
            min="1"
            value={classNumber}
            onChange={(event) => setClassNumber(event.target.value)}
          />
        </div>

        <div className="sb-field">
          <label htmlFor="add-session-duration">Duration (min)</label>
          <input
            id="add-session-duration"
            type="number"
            min="1"
            value={durationMinutes}
            onChange={(event) => setDurationMinutes(event.target.value)}
          />
        </div>

        <div className="sb-field">
          <label htmlFor="add-session-section">Section</label>
          <input
            id="add-session-section"
            type="text"
            placeholder="Optional"
            value={section}
            onChange={(event) => setSection(event.target.value)}
          />
        </div>
      </div>

      {/* Open by default: picking the contents is half the job, and a collapsed
          section read as "there is nothing here". */}
      <details className="cal-addsession-contents" open>
        <summary>
          Contents <span className="sb-selcount">optional — {contentIds.length} selected</span>
        </summary>

        <p className="sb-selcount cal-addsession-hint">
          Leave this empty for a Live class or an exam whose content does not exist yet;
          link it from this session once it appears.
        </p>

        <div className="sb-pickrow">
          <CoursePicker
            courses={courses}
            selectedCourseId={courseId}
            onSelect={(course) => {
              setCourseId(String(course.id));
              setChapter(null);
              setContentIds([]);
            }}
          />
          {/* The mode floats the bucket a class of that kind is scheduled from:
              Live → "Live Class", Exam / Unit Test → "Exam". */}
          <ChapterTreePicker
            courseId={courseId ? Number(courseId) : null}
            selectedChapterId={chapter ? chapter.id : null}
            mode={mode}
            onSelect={(node) => { setChapter(node); setContentIds([]); }}
          />
        </div>

        <ContentRangePicker
          chapterId={chapter ? chapter.id : null}
          chapterPath={chapter ? [...(chapter.path || []), chapter.name] : []}
          batchIds={batchIds}
          mode={mode}
          selectedIds={contentIds}
          onChange={setContentIds}
        />
      </details>

      {error && <div className="cal-error">{error}</div>}

      <div className="sb-form-foot">
        <span className="sb-selcount">
          {canSubmit
            ? `Writes ${batchIds.length} rows on ${isoDate}`
            : 'A subject and a class number are required.'}
        </span>
        <div className="sb-actions">
          <button type="button" className="sb-btn" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="sb-btn sb-btn-primary"
            onClick={submit}
            disabled={!canSubmit}
          >
            {busy ? 'Saving…' : 'Add session'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSessionPanel;
